import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getRunbookScoreboard } from "../lib/api";
import { Printer, ArrowLeft, LayoutGrid } from "lucide-react";
import { BrandLetterhead } from "../components/BrandLetterhead";

const MIG = { pending: "Pending", onboarded: "Onboarded", cutover: "Cutover" };
const E911 = { compliant: "Compliant", review: "Review", pending: "Pending", "non-compliant": "Non-Compliant" };

export default function ScoreboardExport() {
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const track = params.get("track") === "cucm15" ? "cucm15" : "webex";
  const isWebex = track === "webex";
  const navigate = useNavigate();

  useEffect(() => { setData(null); getRunbookScoreboard(track).then(setData).catch(() => {}); }, [track]);

  if (!data) return <div className="min-h-screen bg-background text-zinc-500 font-mono flex items-center justify-center">Loading scoreboard…</div>;

  const sites = data.sites;
  const total = sites.length;
  const cutover = sites.filter((s) => s.mig_status === "cutover").length;
  const complete = sites.filter((s) => s.complete).length;
  const avg = total ? Math.round(sites.reduce((a, s) => a + s.runbook_pct, 0) / total) : 0;
  const waves = new Set(sites.map((s) => s.wave).filter(Boolean));

  const order = [];
  const map = {};
  sites.forEach((s) => { const k = s.wave || "__none__"; if (!map[k]) { map[k] = []; order.push(k); } map[k].push(s); });
  order.sort((a, b) => (a === "__none__" ? 1 : b === "__none__" ? -1 : 0));
  const groups = order.map((k) => ({ wave: k === "__none__" ? "Unassigned" : k, sites: map[k] }));
  const hasWaves = isWebex && waves.size > 0;

  const stats = isWebex
    ? [
        { label: "Locations", value: total },
        { label: "Waves", value: waves.size || "—" },
        { label: "Cutover", value: cutover },
        { label: "Complete", value: complete },
        { label: "Avg runbook", value: `${avg}%` },
      ]
    : [
        { label: "Locations", value: total },
        { label: "Signed off", value: complete },
        { label: "In progress", value: total - complete },
        { label: "Avg runbook", value: `${avg}%` },
      ];

  const gridCols = "grid-cols-[1.5fr_0.9fr_0.7fr_0.7fr_0.9fr_0.6fr_0.8fr_0.7fr]";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="print:hidden border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate(`/scoreboard`)} data-testid="sb-export-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Scoreboard
        </button>
        <button onClick={() => window.print()} data-testid="sb-export-print-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Printer className="h-4 w-4" /> Export / Print PDF
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6 sm:p-8" data-testid="sb-export-page">
        <BrandLetterhead
          title={isWebex ? "Estate Migration Scoreboard" : "CUCM 15 Estate Upgrade Scoreboard"}
          subtitle={isWebex ? "Webex Calling migration — leadership status summary" : "Upgrade to CUCM 15 — leadership status summary"}
        />

        <div className={`grid grid-cols-2 ${isWebex ? "sm:grid-cols-5" : "sm:grid-cols-4"} gap-3 mt-5`}>
          {stats.map((s) => (
            <div key={s.label} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div className="font-heading text-2xl font-extrabold text-white">{s.value}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-md border border-zinc-800 overflow-hidden">
          <div className={`grid ${gridCols} gap-2 px-3 py-2 bg-zinc-950 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold`}>
            <span>Location</span><span>{isWebex ? "Migration" : "Status"}</span><span>Gates</span><span>Runbook</span><span>E911</span><span>Rooms</span><span>Readiness</span><span>Devices</span>
          </div>
          {groups.map((grp) => (
            <React.Fragment key={grp.wave}>
              {hasWaves && (
                <div className="px-3 py-1.5 bg-blue-600/5 border-b border-blue-600/20 text-[11px] uppercase tracking-widest font-bold text-blue-300">
                  {grp.wave} <span className="text-zinc-500 normal-case font-normal tracking-normal">· {grp.sites.length} site(s)</span>
                </div>
              )}
              {grp.sites.map((s) => (
                <div key={s.site_id} className={`grid ${gridCols} gap-2 px-3 py-2 border-b border-zinc-900 text-[12px] items-center`}>
                  <div>
                    <div className="text-zinc-200">{s.name}</div>
                    <div className="text-[10px] text-zinc-500">{s.current_phase}</div>
                  </div>
                  <span className="text-zinc-300">{isWebex ? (MIG[s.mig_status] || s.mig_status) : (s.complete ? "Signed off" : s.current_phase)}</span>
                  <span className="font-mono text-zinc-300">{s.gates_approved}/{s.gates_total}</span>
                  <span className="font-mono text-zinc-300">{s.runbook_pct}%</span>
                  <span className="text-zinc-300">{E911[s.e911_status] || "—"}</span>
                  <span className="font-mono text-zinc-300">{s.rooms_count}</span>
                  <span className="font-mono text-zinc-300">{s.readiness_pct === null ? "—" : `${s.readiness_pct}%`}</span>
                  <span className="font-mono text-zinc-300">{s.device_status === "empty" ? "—" : s.device_status === "blocker" ? `✕ ${s.device_counts?.blocker || 0}` : s.device_status === "warning" ? `⚠ ${(s.device_counts?.warning || 0) + (s.device_counts?.unknown || 0)}` : "✓"}</span>
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center gap-1.5 mt-4 text-[11px] text-zinc-600">
          <LayoutGrid className="h-3 w-3" /> Runbook % = tasks passed across all {isWebex ? "7 PPDIO phases" : "5 CUCM 15 upgrade phases"}. Gates = approved GO/NO-GO checkpoints.
        </div>
      </div>
    </div>
  );
}
